/**
 * Video Player Controller
 * Manages interactive video playback with custom controls
 */

import { getPopupStore, VIDEO_POPUP_ID } from "./PopupController";

type VideoPopupPayload =
	| {
		type: 'file';
		src: string;
		title: string;
	}
	| {
		type: 'embed';
		embed: string;
		title: string;
	};

let videoPopupRegistered = false;

const ensureVideoPopupRegistration = (): void => {
	if (videoPopupRegistered) {
		return;
	}

	getPopupStore().register(VIDEO_POPUP_ID, {
		onClose: () => {
			cleanupFileVideoModal();
			cleanupEmbedVideoModal();
		}
	});

	videoPopupRegistered = true;
};

const bindProgressTracking = (video: HTMLVideoElement): void => {
	if (video.dataset.progressBound === 'true') {
		return;
	}

	video.addEventListener('timeupdate', () => updateProgress(video));
	video.addEventListener('loadedmetadata', () => updateProgress(video));
	video.dataset.progressBound = 'true';
};

const cleanupFileVideoModal = (): void => {
	const video = document.getElementById('modal-video-player');

	if (!video || !(video instanceof HTMLVideoElement)) {
		return;
	}

	video.pause();
	video.currentTime = 0;
	video.src = '';
};

const cleanupEmbedVideoModal = (): void => {
	const container = document.getElementById('modal-embed-container');
	const loader = document.getElementById('embed-loader');

	if (!container) {
		return;
	}

	if (loader) {
		loader.classList.remove('hidden');
	}

	const children = Array.from(container.children);
	children.forEach(child => {
		if (child.id !== 'embed-loader') {
			child.remove();
		}
	});
};

// Open video modal
export function openVideoModal(trigger: HTMLElement): void {
	const videoSrc = trigger.dataset.videoSrc;
	const videoTitle = trigger.dataset.videoTitle;
	
	if (!videoSrc) return;
	const video = document.getElementById('modal-video-player');

	ensureVideoPopupRegistration();
	getPopupStore().open(VIDEO_POPUP_ID, {
		type: 'file',
		src: videoSrc,
		title: videoTitle || ''
	} satisfies VideoPopupPayload as unknown as Record<string, unknown>);
	
	if (video && video instanceof HTMLVideoElement) {
		video.src = videoSrc;
		video.title = videoTitle || '';
		video.muted = false;
		
		// Play video
		video.play().catch(err => console.log('Autoplay prevented:', err));
		bindProgressTracking(video);
	}
}

// Close video modal
export function closeVideoModal(event?: Event): void {
	if (event) {
		event.stopPropagation();
	}

	getPopupStore().close(VIDEO_POPUP_ID);
}

// Open video modal for embed content (YouTube, Vimeo, etc.)
export function openVideoModalEmbed(trigger: HTMLElement): void {
	const embedCode = trigger.dataset.videoEmbed;
	
	if (!embedCode) return;
	const container = document.getElementById('modal-embed-container');
	const loader = document.getElementById('embed-loader');

	ensureVideoPopupRegistration();
	getPopupStore().open(VIDEO_POPUP_ID, {
		type: 'embed',
		embed: embedCode,
		title: trigger.dataset.videoTitle || ''
	} satisfies VideoPopupPayload as unknown as Record<string, unknown>);
	
	if (container) {
		// Show loader
		if (loader) {
			loader.classList.remove('hidden');
		}
		
		// Insert embed code
		const embedWrapper = document.createElement('div');
		embedWrapper.classList.add('absolute', 'inset-0', 'size-full');
		embedWrapper.innerHTML = embedCode;
		const iframe = embedWrapper.querySelector('iframe');
		
		if (iframe) {
			// Hide loader when iframe loads
			iframe.onload = () => {
				if (loader) {
					loader.classList.add('hidden');
				}
			};
			
			// Add timeout fallback (hide loader after 3 seconds even if onload doesn't fire)
			setTimeout(() => {
				if (loader) {
					loader.classList.add('hidden');
				}
			}, 3000);
		}
		
		container.appendChild(embedWrapper);
	}
}

// Close video modal for embed content
export function closeVideoModalEmbed(event?: Event): void {
	if (event) {
		event.stopPropagation();
	}

	getPopupStore().close(VIDEO_POPUP_ID);
}

// Load embed video on poster click (lazy loading)
export function loadEmbedVideo(trigger: HTMLElement): void {
	const embedCode = trigger.dataset.videoEmbed;
	const section = trigger.closest('section');
	if (!embedCode) return;
	
	const sectionID = section?.getAttribute('id');
	const embedContainer = trigger.querySelector('.embed-container');
	const poster = trigger.querySelector('img');
	const playButton = trigger.querySelector('.absolute.inset-0.flex');
	const caption = document.getElementById(`caption-${sectionID}`);
	const mediaCaption = document.getElementById(`media-caption-${sectionID}`);
	
	if (embedContainer && poster && playButton) {
		// Insert embed code
		embedContainer.innerHTML = embedCode;
		
		// Show embed, hide poster and play button
		embedContainer.classList.remove('hidden');
		poster.classList.add('hidden');
		playButton.classList.add('hidden');
		
		// Remove click handler
		trigger.style.cursor = 'default';
		trigger.onclick = null;

		if (caption) {
			caption.classList.add('hidden');
		}

		if (mediaCaption) {
			mediaCaption.classList.remove(
				"rounded-bl-xl", "lg:rounded-bl-3xl",
				"rounded-br-xl", "lg:rounded-br-3xl",
				"rounded-t-xl", "lg:rounded-t-3xl"
			);
			mediaCaption.classList.add("rounded-xl", "lg:rounded-3xl");
		}
	}
}

// Seek video to specific time on progress bar click
export function seekVideo(event: MouseEvent, progressContainer: HTMLElement): void {
	event.stopPropagation();
	const container = progressContainer.closest('.group');
	const video = container?.querySelector('video');
	
	if (video && video.duration) {
		const rect = progressContainer.getBoundingClientRect();
		const clickX = event.clientX - rect.left;
		const progress = clickX / rect.width;
		video.currentTime = progress * video.duration;
	}
}

// Toggle play/pause state
export function togglePlayPause(video: HTMLVideoElement): void {
	const container = video.closest('.group');
	const playIcon = container?.querySelector('.video-play-icon');
	const playSvg = playIcon?.querySelector('.play-icon');
	const pauseSvg = playIcon?.querySelector('.pause-icon');
	
	if (video.paused) {
		video.play();
		playSvg?.classList.add('hidden');
		pauseSvg?.classList.remove('hidden');
	} else {
		video.pause();
		playSvg?.classList.remove('hidden');
		pauseSvg?.classList.add('hidden');
	}
	
	// Show icon for 500ms
	playIcon?.classList.remove('opacity-0');
	playIcon?.classList.add('opacity-100');
	setTimeout(() => {
		playIcon?.classList.remove('opacity-100');
		playIcon?.classList.add('opacity-0');
	}, 500);
}

// Toggle mute/unmute state
export function toggleMute(button: HTMLElement): void {
	const container = button.closest('.group');
	const video = container?.querySelector('video');
	const muteIcon = button.querySelector('.mute-icon');
	const unmuteIcon = button.querySelector('.unmute-icon');
	
	if (video) {
		if (video.muted) {
			video.muted = false;
			muteIcon?.classList.add('hidden');
			unmuteIcon?.classList.remove('hidden');
		} else {
			video.muted = true;
			muteIcon?.classList.remove('hidden');
			unmuteIcon?.classList.add('hidden');
		}
	}
}

// Update progress bar based on video current time
function updateProgress(video: HTMLVideoElement): void {
	const progressBar = video.closest('.group')?.querySelector<HTMLElement>('.video-progress');
	if (progressBar && video.duration) {
		const progress = video.currentTime / video.duration;
		progressBar.style.transform = `scaleX(${progress})`;
	}
}

// Initialize video player with Intersection Observer for lazy loading
function initVideoPlayer(): void {
	if (!('IntersectionObserver' in window)) {
		return;
	}

	const videoObserver = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			const video = entry.target;
			if (!(video instanceof HTMLVideoElement)) return;
			if (video.getAttribute('data-autoplay') !== 'true') {
				// remove observer for videos that don't have autoplay enabled
				videoObserver.unobserve(video);
				return;
			};
			if (entry.isIntersecting) {
				// Video in viewport - play
				video.play().catch(err => console.log('Autoplay prevented:', err));
			} else {
				// Video out of viewport - pause
				video.pause();
			}
		});
	}, {
		threshold: 0.5, // Trigger when 50% of video is visible
		rootMargin: '0px'
	});

	// Apply observer to all lazy-load videos
	document.querySelectorAll<HTMLVideoElement>('.video-lazy').forEach(video => {
		videoObserver.observe(video);
		bindProgressTracking(video);
	});
}

// Make functions globally available for inline onclick handlers
declare global {
	interface Window {
		seekVideo: typeof seekVideo;
		togglePlayPause: typeof togglePlayPause;
		toggleMute: typeof toggleMute;
		openVideoModal: typeof openVideoModal;
		closeVideoModal: typeof closeVideoModal;
		openVideoModalEmbed: typeof openVideoModalEmbed;
		closeVideoModalEmbed: typeof closeVideoModalEmbed;
		loadEmbedVideo: typeof loadEmbedVideo;
	}
}

window.seekVideo = seekVideo;
window.togglePlayPause = togglePlayPause;
window.toggleMute = toggleMute;
window.openVideoModal = openVideoModal;
window.closeVideoModal = closeVideoModal;
window.openVideoModalEmbed = openVideoModalEmbed;
window.closeVideoModalEmbed = closeVideoModalEmbed;
window.loadEmbedVideo = loadEmbedVideo;

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initVideoPlayer);
} else {
	initVideoPlayer();
}
